import React from 'react';
import './CoachStrategyProposalCard.css';
import { formatNextScheduledWorkout, getNextScheduledWorkout } from '../utils/nextScheduledWorkout';
import { shouldMarqueeMatrixActivityName } from './activities/activityNameMarquee';
import {
  mediaGlobalConstraintKey,
  mergeMediaGlobalConstraints,
  splitMediaRequirementItems
} from './activities/mediaRequirementContract';

const proposalTitle = (kind) => ({
  nutrition_target_adjustment: 'Adjust your personal nutrition targets',
  nutrition_logging_evidence_request: 'Gather nutrition evidence',
  metric_cadence_adjustment: 'Increase bodyweight check-in frequency',
  dojo_workout_plan_adjustment: 'Adjust dojo workout plan',
  member_workout_plan_adjustment: 'Adjust your workout plan',
  media_requirements_adjustment: 'Update progress-photo requirements'
}[kind] || 'Strategy proposal');

const NUTRITION_OVERLAY_NOTICE = 'Only your personal nutrition targets will change. Your dojo’s shared meal schedule and meal templates will stay the same.';

const numericTarget = (value) => (Number.isFinite(Number(value)) ? Number(value) : null);

// Presentation-only numeric policy. Keep canonical proposal values numeric;
// never expose JavaScript floating-point subtraction artifacts to members.
const formatNumber = (value, maximumFractionDigits) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return null;
  return number.toLocaleString(undefined, {
    maximumFractionDigits,
    minimumFractionDigits: 0
  });
};

const formatBodyweight = (value) => formatNumber(value, 1);

const formatProposalReason = (proposal) => {
  const reason = String(proposal?.reason || '');
  const trend = proposal?.evidence?.weight_trend_lbs;
  if (!Number.isFinite(Number(trend))) return reason;

  // Persisted proposals may contain a rationale generated before the
  // backend formatter was deployed. Re-render the known numeric field from
  // canonical evidence so historical records do not leak float artifacts.
  return reason.replace(
    /bodyweight declined ([+-]?(?:\d+\.?\d*|\.\d+)) lb/i,
    `bodyweight declined ${formatBodyweight(Math.abs(Number(trend)))} lb`
  );
};

const formatDailyTargets = (targets) => {
  if (!targets || typeof targets !== 'object') return '';
  const fields = [
    ['calories', 'kcal/day'],
    ['protein_g', 'g protein'],
    ['carbs_g', 'g carbs'],
    ['fat_g', 'g fat']
  ];
  return fields
    .map(([key, label]) => {
      const value = numericTarget(targets[key]);
      const formatted = value === null ? null : formatNumber(value, 2);
      return formatted === null ? null : `${formatted}${key === 'calories' ? ` ${label}` : label}`;
    })
    .filter(Boolean)
    .join(' · ');
};

const nutritionTargets = (proposal, prefix, legacyCalories) => {
  const target = proposal?.[`${prefix}_daily_targets`]
    || proposal?.[`${prefix === 'old' ? 'current' : 'proposed'}_daily_targets`];
  if (target && typeof target === 'object') return target;
  return numericTarget(proposal?.[legacyCalories]) === null ? null : { calories: proposal[legacyCalories] };
};

const formatExercisePrescription = (exercise) => {
  const name = String(exercise?.name || '').trim();
  if (!name) return null;
  const sets = exercise?.sets ?? exercise?.set_count;
  const reps = exercise?.reps ?? exercise?.rep_range;
  const duration = exercise?.duration_seconds ? `${Math.round(Number(exercise.duration_seconds) / 60)} min` : null;
  const prescription = [sets ? `${sets} sets` : null, reps ? `${reps} reps` : null, duration].filter(Boolean).join(' · ');
  return { name, prescription, status: exercise?.proposal_status === 'retained' ? 'retained' : 'adjusted' };
};

const compactSchedulePresentation = (schedule) => {
  if (!schedule) return null;
  if (schedule.isToday) return { ...schedule, primary: 'TODAY', secondary: 'Scheduled today' };
  const [primary, completion] = String(schedule.label || '').split(/,\s*(?=within\s)/i);
  const completionWindow = completion
    ? completion.replace(/^within\s+/i, '').replace(/^(\d+)\s+days?$/i, '$1-day') + ' window'
    : 'Recurring';
  return {
    ...schedule,
    primary: primary || schedule.label,
    secondary: completionWindow
  };
};

const workoutChangeDetails = (changes) => (Array.isArray(changes) ? changes : []).map((change, index) => {
  const nextDate = getNextScheduledWorkout(change?.schedule);
  const schedule = formatNextScheduledWorkout(change?.schedule);
  const removedExercises = (Array.isArray(change?.removed_exercises) ? change.removed_exercises : [])
    .filter(removal => removal?.exercise_id && removal?.reason)
    .map(removal => ({ id: removal.exercise_id, name: removal.name || removal.exercise_id, reason: removal.reason }));
  return {
    id: change?.entry_id || `${change?.name || 'workout'}-${index}`,
    name: String(change?.name || `Workout ${index + 1}`).trim(),
    ...(schedule ? { schedule: compactSchedulePresentation(schedule) } : {}),
    ...(nextDate ? { nextDate: nextDate.date.getTime() } : {}),
    ...(removedExercises.length > 0 ? { removedExercises } : {}),
    exercises: (Array.isArray(change?.exercises) ? change.exercises : []).map(formatExercisePrescription).filter(Boolean)
  };
}).sort((left, right) => {
  if (left.schedule?.isToday !== right.schedule?.isToday) return left.schedule?.isToday ? -1 : 1;
  if (left.nextDate != null && right.nextDate != null) return left.nextDate - right.nextDate;
  if (left.nextDate != null) return -1;
  if (right.nextDate != null) return 1;
  return 0;
});

const formatChanges = (changes) => {
  const details = workoutChangeDetails(changes);
  if (details.length === 0) return 'Review the proposed workout sessions';
  const total = details.reduce((sum, workout) => sum + workout.exercises.length, 0);
  const adjusted = details.reduce((sum, workout) => sum + workout.exercises.filter(exercise => exercise.status === 'adjusted').length, 0);
  return `${details.length} workout${details.length === 1 ? '' : 's'} · ${adjusted} exercise${adjusted === 1 ? '' : 's'} adjusted · ${total} exercise${total === 1 ? '' : 's'} after approval`;
};

const mediaRequirementKey = (value) => {
  const text = String(value || '').toLowerCase().replace(/\s+/g, ' ').trim();
  return /\bleg(s)?\b|quad|hamstring|calf|lower\s+body/.test(text) ? 'lower-body-coverage' : text;
};

const formatEvidence = (proposal) => {
  const evidence = proposal?.evidence || {};
  const lines = [];
  if (Number.isFinite(Number(evidence.weight_trend_lbs)) && Number(evidence.window_days) > 0) {
    lines.push(`Weight trend: ${formatBodyweight(evidence.weight_trend_lbs)} lb over ${Number(evidence.window_days)} days`);
  }
  const history = evidence.meal_history;
  const comparison = evidence.comparison;
  if (comparison && Number.isFinite(comparison.mean_logged_calories) && Number.isFinite(comparison.mean_effective_target)) {
    lines.push(`Logged intake: ${Math.round(comparison.mean_logged_calories).toLocaleString()} kcal/day vs dated effective targets: ${Math.round(comparison.mean_effective_target).toLocaleString()} kcal/day (${comparison.complete_days} complete days)`);
    lines.push(`Weight trend: ${(comparison.weight_slope_lbs_per_day * 7).toFixed(2)} lb/week over ${comparison.window_days} days; ${evidence.weights?.length || 0} weigh-in days`);
  }
  if (Number.isFinite(proposal?.delta_calories)) lines.push(`Change: ${proposal.delta_calories > 0 ? '+' : ''}${proposal.delta_calories} kcal/day`);
  if (proposal?.uncertainty) lines.push(`Uncertainty: ${proposal.uncertainty}`);
  if (proposal?.effective_from === 'approval') {
    lines.push(`Effective only on your approval, not backdated. Reassess ${proposal.review_after_days} days after approval.`);
    if (proposal.monitoring_goal) lines.push(proposal.monitoring_goal);
  }
  if (history && Number(history.logged_days) >= 0) {
    const adherence = Number(history.adherence_ratio);
    const adherenceText = Number.isFinite(adherence) ? ` (${Math.round(adherence * 100)}% adherence)` : '';
    lines.push(`Meal logging: ${Number(history.logged_days)} days${adherenceText}`);
  }
  const coverage = evidence.muscle_group_coverage;
  if (coverage && typeof coverage === 'object') {
    Object.entries(coverage).forEach(([key, value]) => {
      lines.push(`Workout coverage: ${key.replace(/_/g, ' ')}: ${value}`);
    });
  }
  return lines;
};

const formatCadence = (number, unit) => {
  const count = Number(number) || 1;
  const normalizedUnit = String(unit || '').replace(/s$/, '');
  return count === 1 ? `every ${normalizedUnit}` : `every ${count} ${normalizedUnit}s`;
};

/**
 * Convert a persisted proposal record into display-only UI data.
 * Contract: proposals are returned from coach home as `{ proposals: Proposal[] }`,
 * where each record has `id` or `_id`, `kind`, `status`, `reason`, and `evidence`.
 * Nutrition-target proposals are member-scoped overlays: `old_daily_targets` and
 * `new_daily_targets` may include calories, protein_g, carbs_g, and fat_g. The
 * legacy calorie-only fields remain supported while producers migrate. They do
 * not change a dojo's shared meal schedule or its meal templates.
 */
export const presentStrategyProposal = (proposal) => {
  const kind = proposal?.kind;
  const nutrition = kind === 'nutrition_target_adjustment';
  const nutritionEvidenceRequest = kind === 'nutrition_logging_evidence_request';
  const metricCadence = kind === 'metric_cadence_adjustment';
  const workout = kind === 'dojo_workout_plan_adjustment' || kind === 'member_workout_plan_adjustment';
  const mediaRequirements = kind === 'media_requirements_adjustment';
  const currentTargets = nutrition ? nutritionTargets(proposal, 'old', 'old_daily_calories') : null;
  const proposedTargets = nutrition ? nutritionTargets(proposal, 'new', 'new_daily_calories') : null;
  const currentMediaContract = mediaRequirements ? splitMediaRequirementItems(proposal?.current_requirements) : { views: [], globalConstraints: [] };
  const proposedMediaContract = mediaRequirements ? splitMediaRequirementItems(proposal?.proposed_requirements) : { views: [], globalConstraints: [] };
  const currentMedia = currentMediaContract.views;
  const proposedMedia = proposedMediaContract.views;
  const currentGlobalConstraints = mediaRequirements
    ? mergeMediaGlobalConstraints(currentMediaContract.globalConstraints, proposal?.current_global_constraints)
    : [];
  const proposedGlobalConstraints = mediaRequirements
    ? mergeMediaGlobalConstraints(proposedMediaContract.globalConstraints, proposal?.proposed_global_constraints)
    : [];
  const globalConstraintAdditions = proposedGlobalConstraints.filter(constraint => (
    !currentGlobalConstraints.some(current => (
      (mediaGlobalConstraintKey(current) || mediaRequirementKey(current))
      === (mediaGlobalConstraintKey(constraint) || mediaRequirementKey(constraint))
    ))
  ));
  const mediaUpdates = mediaRequirements && Array.isArray(proposal?.requirement_updates)
    ? proposal.requirement_updates.filter(update => update?.from && update?.to)
    : [];
  const updateTargetKeys = new Set(mediaUpdates.map(update => mediaRequirementKey(update.to)));
  const mediaChanges = mediaRequirements
    ? proposedMedia.filter(requirement => (
        !updateTargetKeys.has(mediaRequirementKey(requirement))
        && !currentMedia.some(current => mediaRequirementKey(current) === mediaRequirementKey(requirement))
      ))
    : [];
  const explicitReplacement = mediaRequirements && proposal?.replacement_requested === true;
  const mediaRemovals = explicitReplacement
    ? (Array.isArray(proposal?.removed_requirements) && proposal.removed_requirements.length > 0
      ? proposal.removed_requirements
      : currentMedia.filter(requirement => !proposedMedia.some(proposed => mediaRequirementKey(proposed) === mediaRequirementKey(requirement))))
    : [];
  // A media proposal is additive by default. The complete resulting checklist
  // is always derived from the current checklist plus additions, never from an
  // incomplete model-generated proposed list.
  const updatedMedia = currentMedia.map(requirement => {
    const update = mediaUpdates.find(item => mediaRequirementKey(item.from) === mediaRequirementKey(requirement));
    return update?.to || requirement;
  });
  const mediaAfterApproval = mediaRequirements
    ? (explicitReplacement ? proposedMedia : [...updatedMedia, ...mediaChanges])
        .filter((requirement, index, all) => all.findIndex(item => mediaRequirementKey(item) === mediaRequirementKey(requirement)) === index)
    : [];
  const workoutDetails = workout ? workoutChangeDetails(proposal?.proposed_changes) : [];
  const metricName = String(proposal?.metric_name || 'Bodyweight');
  return {
    id: proposal?.id || proposal?._id,
    title: proposalTitle(kind),
    reason: formatProposalReason(proposal),
    evidence: formatEvidence(proposal),
    latestEvidence: proposal?.latest_evidence ? formatEvidence({ evidence: proposal.latest_evidence }) : [],
    latestEvidenceNote: String(proposal?.latest_evidence_note || ''),
    current: nutrition
      ? formatDailyTargets(currentTargets)
      : (metricCadence
          ? `${metricName} · ${formatCadence(proposal?.current_recurrence_number, proposal?.current_recurrence_unit)}`
          : (workout ? (proposal?.scope === 'dojo' ? 'Current dojo workout plan' : 'Current workout plan') : (mediaRequirements ? (currentMedia.join(' · ') || 'Current photo requirements') : ''))),
    proposed: nutrition
      ? formatDailyTargets(proposedTargets)
      : (metricCadence
          ? `${metricName} · ${formatCadence(proposal?.proposed_recurrence_number, proposal?.proposed_recurrence_unit)}`
          : (workout ? formatChanges(proposal?.proposed_changes) : undefined)),
    // Nutrition and cadence rows show the new value; workout rows summarize changes.
    proposedLabel: nutrition || metricCadence ? 'Proposed' : 'Summary',
    workoutDetails,
    additions: mediaRequirements ? (mediaChanges.length > 0 ? mediaChanges.join(' · ') : 'No new photo additions') : undefined,
    afterApproval: mediaRequirements ? (mediaAfterApproval.join(' · ') || 'No photo requirements') : undefined,
    mediaRequirements,
    mediaRemovalLabel: explicitReplacement && mediaRemovals.length > 0 ? 'Removals' : '',
    mediaRemovalNotice: explicitReplacement && mediaRemovals.length > 0 ? 'The coach explicitly requested removing these requirements.' : '',
    mediaCurrentItems: mediaRequirements ? currentMedia : [],
    mediaUpdateItems: mediaRequirements ? mediaUpdates : [],
    mediaAdditionItems: mediaRequirements ? mediaChanges : [],
    mediaRemovalItems: mediaRequirements ? mediaRemovals : [],
    mediaAfterApprovalItems: mediaRequirements ? mediaAfterApproval : [],
    mediaGlobalConstraintHeading: mediaRequirements ? 'Applies to every photo' : '',
    mediaGlobalConstraintItems: mediaRequirements ? proposedGlobalConstraints : [],
    mediaGlobalConstraintAdditionItems: mediaRequirements ? globalConstraintAdditions : [],
    scopeNotice: nutrition && proposal?.scope === 'member' ? NUTRITION_OVERLAY_NOTICE : '',
    scopeLabel: nutritionEvidenceRequest ? 'No targets will change' : (proposal?.scope === 'member' ? 'Personal change' : (proposal?.scope === 'dojo' ? 'Dojo-wide change' : '')),
    approveLabel: metricCadence ? 'Approve check-in cadence' : (proposal?.scope === 'dojo' ? 'Approve dojo plan' : (mediaRequirements ? 'Approve photo requirements' : (workout ? 'Approve workout changes' : 'Approve personal targets'))),
    dismissLabel: nutritionEvidenceRequest ? 'Got it' : (metricCadence ? 'Decline cadence change' : (proposal?.scope === 'dojo' ? 'Decline dojo plan' : 'Decline personal change')),
    commentLabel: nutritionEvidenceRequest ? 'Ask Coach' : 'Comment',
    isAcknowledgement: nutritionEvidenceRequest,
    canApprove: !nutritionEvidenceRequest && proposal?.can_approve !== false && ((nutrition && proposal?.scope === 'member') || metricCadence || (workout && (proposal?.scope === 'dojo' || proposal?.scope === 'member')) || mediaRequirements),
    canRespond: proposal?.can_respond !== false,
    requiresConfirmation: proposal?.requires_confirmation === true,
    requiresChiefApproval: proposal?.requires_chief_approval === true
  };
};

export const getPendingStrategyProposals = (coachHome) => (
  Array.isArray(coachHome?.proposals)
    ? coachHome.proposals.filter((proposal) => proposal?.status === 'pending')
    : []
);

// Evidence requests are guidance, not proposed changes. Their primary action
// must never be wired to the reject lifecycle.
export const proposalResponseAction = (presentation) => (
  presentation?.isAcknowledgement ? 'acknowledge' : 'reject'
);

export const createProposalCommentPrefill = (proposal) => {
  const presentation = presentStrategyProposal(proposal);
  return `Regarding proposal ${presentation.id} (${presentation.title}): `;
};

/**
 * Reusable proposal record card. Persistence is deliberately owned by callers:
 * onApprove/onDismiss/onComment receive the original proposal record so this UI
 * can be connected once the proposal API contract is available.
 */
export const CoachStrategyProposalCard = ({ proposal, onApprove, onDismiss, onAcknowledge, onComment, disabled = false }) => {
  const presentation = presentStrategyProposal(proposal);
  if (!presentation.id) return null;

  return (
    <article className="coach-strategy-proposal-card" aria-label={presentation.title}>
      <div className="coach-strategy-proposal-card__heading">
        <span aria-hidden="true">🧭</span>
        <strong>{presentation.title}</strong>
      </div>
      {presentation.scopeLabel && <div className="coach-strategy-proposal-card__approval">{presentation.scopeLabel}</div>}
      {presentation.requiresChiefApproval && (
        <div className="coach-strategy-proposal-card__approval">Chief approval required</div>
      )}
      {presentation.reason && <p className="coach-strategy-proposal-card__reason">{presentation.reason}</p>}
      {presentation.scopeNotice && <p className="coach-strategy-proposal-card__scope-notice">{presentation.scopeNotice}</p>}
      {(presentation.current || presentation.proposed) && !presentation.mediaRequirements && (
        <dl className="coach-strategy-proposal-card__comparison">
          {presentation.current && <><dt>Current</dt><dd>{presentation.current}</dd></>}
          {presentation.proposed && <><dt>{presentation.proposedLabel}</dt><dd>{presentation.proposed}</dd></>}
          {presentation.additions && <><dt>Additions</dt><dd>{presentation.additions}</dd></>}
          {presentation.afterApproval && <><dt>After approval</dt><dd>{presentation.afterApproval}</dd></>}
        </dl>
      )}
      {presentation.workoutDetails.length > 0 && (
        <div className="coach-strategy-proposal-card__workout-diff">
          <div className="coach-strategy-proposal-card__workout-diff-heading">Proposed workout changes</div>
          {presentation.workoutDetails.map((workout) => (
            <details className="coach-strategy-proposal-card__workout" key={workout.id}>
              <summary>
                {workout.schedule && (
                  <span className={`coach-strategy-proposal-card__schedule ${workout.schedule.isToday ? 'is-today' : ''}`}>
                    <strong>{workout.schedule.primary}</strong>
                    <small>{workout.schedule.secondary}</small>
                  </span>
                )}
                <span className={`coach-strategy-proposal-card__workout-title ${shouldMarqueeMatrixActivityName(workout.name) ? 'is-marquee' : ''}`}>
                  <span className="coach-strategy-proposal-card__workout-title-track">
                    <span>{workout.name}</span>
                    {shouldMarqueeMatrixActivityName(workout.name) && <span aria-hidden="true">{workout.name}</span>}
                  </span>
                </span>
                <span className="coach-strategy-proposal-card__metrics">
                  <span><strong>{workout.exercises.length}</strong> exercises</span>
                  <span><strong>{workout.exercises.filter(exercise => exercise.status === 'adjusted').length}</strong> adjusted</span>
                  {(workout.removedExercises || []).length > 0 && <span className="is-removal"><strong>{workout.removedExercises.length}</strong> removed</span>}
                </span>
                <span className="coach-strategy-proposal-card__chevron" aria-hidden="true">›</span>
              </summary>
              <ul>
                {workout.exercises.map((exercise) => (
                  <li className={`coach-strategy-proposal-card__exercise--${exercise.status}`} key={exercise.name}>
                    {exercise.status === 'adjusted' ? <strong>+ {exercise.name}</strong> : <span className="coach-strategy-proposal-card__retained-name">• {exercise.name}</span>}
                    {exercise.prescription && <span>{exercise.prescription}</span>}
                  </li>
                ))}
                {(workout.removedExercises || []).map((exercise) => (
                  <li className="coach-strategy-proposal-card__exercise--removed" key={`removed-${exercise.id}`}>
                    <strong>− {exercise.name}</strong>
                    <span>{exercise.reason}</span>
                  </li>
                ))}
              </ul>
            </details>
          ))}
        </div>
      )}
      {presentation.mediaRequirements && (
        <div className="coach-strategy-proposal-card__media-requirements">
          <div className="coach-strategy-proposal-card__media-section">
            <strong>Current requirements</strong>
            <ul>{presentation.mediaCurrentItems.map(item => <li key={`current-${item}`}>{item}</li>)}</ul>
          </div>
          {presentation.mediaUpdateItems.length > 0 && (
            <div className="coach-strategy-proposal-card__media-section coach-strategy-proposal-card__media-section--updates">
              <strong>Edits</strong>
              <ul>{presentation.mediaUpdateItems.map(update => (
                <li key={`update-${update.from}`}>
                  <span className="coach-strategy-proposal-card__media-edit-from">{update.from}</span>
                  <span aria-hidden="true"> → </span>
                  <strong>{update.to}</strong>
                  {update.reason && <small>{update.reason}</small>}
                </li>
              ))}</ul>
            </div>
          )}
          {presentation.mediaAdditionItems.length > 0 && (
            <div className="coach-strategy-proposal-card__media-section">
              <strong>Additions</strong>
              <ul>{presentation.mediaAdditionItems.map(item => <li key={`addition-${item}`}>{item}</li>)}</ul>
            </div>
          )}
          {presentation.mediaGlobalConstraintItems.length > 0 && (
            <div className="coach-strategy-proposal-card__media-section coach-strategy-proposal-card__media-section--global">
              <strong>{presentation.mediaGlobalConstraintHeading}</strong>
              <ul>{presentation.mediaGlobalConstraintItems.map(item => (
                <li key={`global-${item}`}>
                  {item}
                  {presentation.mediaGlobalConstraintAdditionItems.includes(item) && <small>New</small>}
                </li>
              ))}</ul>
            </div>
          )}
          {presentation.mediaRemovalItems.length > 0 && (
            <div className="coach-strategy-proposal-card__media-section coach-strategy-proposal-card__media-section--warning">
              <strong>{presentation.mediaRemovalLabel}</strong>
              <ul>{presentation.mediaRemovalItems.map(item => <li key={`removal-${item}`}>{item}</li>)}</ul>
              <p>{presentation.mediaRemovalNotice}</p>
            </div>
          )}
          <div className="coach-strategy-proposal-card__media-section">
            <strong>After approval</strong>
            <ul>{presentation.mediaAfterApprovalItems.map(item => <li key={`after-${item}`}>{item}</li>)}</ul>
          </div>
        </div>
      )}
      {presentation.evidence.length > 0 && (
        <ul className="coach-strategy-proposal-card__evidence">
          {presentation.evidence.map((line) => <li key={line}>{line}</li>)}
        </ul>
      )}
      {(presentation.latestEvidenceNote || presentation.latestEvidence.length > 0) && (
        <div className="coach-strategy-proposal-card__latest-evidence">
          <strong>Latest evidence</strong>
          {presentation.latestEvidenceNote && <p>{presentation.latestEvidenceNote}</p>}
          {presentation.latestEvidence.length > 0 && (
            <ul>{presentation.latestEvidence.map((line) => <li key={`latest-${line}`}>{line}</li>)}</ul>
          )}
        </div>
      )}
      <div className="coach-strategy-proposal-card__actions">
        {presentation.canApprove && (
          <button type="button" onClick={() => onApprove?.(proposal)} disabled={disabled}>{presentation.approveLabel}</button>
        )}
        {presentation.canRespond && <button
          type="button"
          onClick={() => (proposalResponseAction(presentation) === 'acknowledge' ? onAcknowledge : onDismiss)?.(proposal)}
          disabled={disabled}
        >{presentation.dismissLabel}</button>}
        {presentation.canRespond && <button type="button" onClick={() => onComment?.(proposal)} disabled={disabled}>{presentation.commentLabel}</button>}
      </div>
    </article>
  );
};

export default CoachStrategyProposalCard;
